import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  Access,
  ApiClient,
  ApiClientSecret,
  AlertRow,
  AlertRule,
  AlertSummary,
  Dashboard,
  DocumentDetail,
  DocumentRow,
  AddressingCheck,
  DirectoryCompany,
  DirectoryEntry,
  DocumentSummary,
  InvoiceContent,
  NormalizedStatus,
  PagedResult,
  StatusMapping,
  AppStatus,
  AppStatusRequest,
  AppStatusUsage,
  SyncError,
  SyncRun,
  SyncStatus,
} from './api.models';
import { DocumentFilters, toHttpParams } from './document-filters';

export type DocumentSort =
  | 'ReceivedAt'
  | 'IssueDate'
  | 'Number'
  | 'Amount'
  | 'StatusChangedAt'
  | 'Company'
  | 'Sender'
  | 'Receiver';

/** Fichier téléchargé (export, XML). */
export interface DownloadedFile {
  blob: Blob;
  fileName: string;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);

  documents(
    filters: DocumentFilters,
    page: number,
    pageSize: number,
    sort: DocumentSort,
    desc: boolean,
  ): Observable<PagedResult<DocumentRow>> {
    return this.http.get<PagedResult<DocumentRow>>('/api/documents', {
      params: toHttpParams(filters, { page, pageSize, sort, desc }),
    });
  }

  /** Bilan (total, reçues, émises, anomalies, montants) sur les mêmes filtres que la liste. */
  documentsSummary(filters: DocumentFilters): Observable<DocumentSummary> {
    return this.http.get<DocumentSummary>('/api/documents/summary', {
      params: toHttpParams(filters),
    });
  }

  /** Sociétés émettrices ou destinataires des documents accessibles dont le nom contient `q`. */
  parties(
    side: 'sender' | 'receiver',
    q: string,
  ): Observable<{ name: string; documents: number }[]> {
    return this.http.get<{ name: string; documents: number }[]>('/api/documents/parties', {
      params: { side, q },
    });
  }

  /** Statuts bruts Flowie rencontrés dans les sociétés accessibles, les plus fréquents d'abord. */
  rawStatuses(): Observable<{ rawStatus: string; documents: number }[]> {
    return this.http.get<{ rawStatus: string; documents: number }[]>('/api/documents/raw-statuses');
  }

  document(id: string): Observable<DocumentDetail> {
    return this.http.get<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}`);
  }

  exportDocuments(
    filters: DocumentFilters,
    format: 'xlsx' | 'csv',
    sort: DocumentSort,
    desc: boolean,
  ): Observable<DownloadedFile> {
    return this.download(
      '/api/documents/export',
      toHttpParams(filters, { format, sort, desc }),
      `documents.${format}`,
    );
  }

  xml(documentId: string, companyId: string): Observable<DownloadedFile> {
    return this.download(
      `/api/documents/${encodeURIComponent(documentId)}/xml`,
      new HttpParams({ fromObject: { companyId } }),
      `${documentId}.xml`,
    );
  }

  /** Pièce jointe embarquée dans le XML, extraite à la demande. */
  attachment(
    documentId: string,
    index: number,
    companyId: string,
    fileName: string,
  ): Observable<DownloadedFile> {
    return this.download(
      `/api/documents/${encodeURIComponent(documentId)}/attachments/${index}`,
      new HttpParams({ fromObject: { companyId } }),
      fileName,
    );
  }

  /** Contenu de la facture extrait du XML UBL (lu chez Flowie à la demande). */
  invoice(documentId: string, companyId: string): Observable<InvoiceContent> {
    return this.http.get<InvoiceContent>(
      `/api/documents/${encodeURIComponent(documentId)}/invoice`,
      {
        params: { companyId },
      },
    );
  }

  /** Vérifie le code d'adressage de la facture dans l'annuaire PPF du destinataire. */
  checkAddressing(documentId: string, companyId: string): Observable<AddressingCheck> {
    return this.http.post<AddressingCheck>(
      `/api/documents/${encodeURIComponent(documentId)}/addressing-check`,
      null,
      { params: { companyId } },
    );
  }

  directorySearch(q: string): Observable<DirectoryCompany[]> {
    return this.http.get<DirectoryCompany[]>('/api/directory/search', { params: { q } });
  }

  directoryEntry(siren: string): Observable<DirectoryEntry> {
    return this.http.get<DirectoryEntry>(`/api/directory/${encodeURIComponent(siren)}`);
  }

  dashboard(filters: DocumentFilters, days: number): Observable<Dashboard> {
    return this.http.get<Dashboard>('/api/dashboard', { params: toHttpParams(filters, { days }) });
  }

  // --- Alertes ---

  alerts(query: {
    rule: AlertRule | null;
    resolved: boolean;
    unreadOnly: boolean;
    page: number;
    pageSize: number;
  }): Observable<PagedResult<AlertRow>> {
    let params = new HttpParams({
      fromObject: {
        resolved: query.resolved,
        unreadOnly: query.unreadOnly,
        page: query.page,
        pageSize: query.pageSize,
      },
    });
    if (query.rule) params = params.set('rule', query.rule);
    return this.http.get<PagedResult<AlertRow>>('/api/alerts', { params });
  }

  alertSummary(): Observable<AlertSummary> {
    return this.http.get<AlertSummary>('/api/alerts/summary');
  }

  /** Marque comme lues des alertes (ids) ou toutes les alertes actives de la règle. */
  markAlertsRead(request: {
    ids?: number[];
    rule?: AlertRule | null;
  }): Observable<{ marked: number }> {
    return this.http.post<{ marked: number }>('/api/alerts/read', {
      ids: request.ids ?? null,
      rule: request.rule ?? null,
    });
  }

  // --- Administration ---

  syncStatus(): Observable<SyncStatus> {
    return this.http.get<SyncStatus>('/api/admin/sync');
  }

  syncRuns(page = 1, pageSize = 20): Observable<PagedResult<SyncRun>> {
    return this.http.get<PagedResult<SyncRun>>('/api/admin/sync/runs', {
      params: { page, pageSize },
    });
  }

  syncRun(id: number): Observable<{ run: SyncRun; errors: SyncError[] }> {
    return this.http.get<{ run: SyncRun; errors: SyncError[] }>(`/api/admin/sync/runs/${id}`);
  }

  triggerSync(fullScan: boolean): Observable<unknown> {
    return this.http.post('/api/admin/sync', null, { params: { fullScan } });
  }

  access(): Observable<Access[]> {
    return this.http.get<Access[]>('/api/admin/access');
  }

  grantAccess(userPrincipalName: string, companyIds: string[]): Observable<Access[]> {
    return this.http.post<Access[]>('/api/admin/access', { userPrincipalName, companyIds });
  }

  revokeAccess(id: number): Observable<unknown> {
    return this.http.delete(`/api/admin/access/${id}`);
  }

  apiClients(): Observable<ApiClient[]> {
    return this.http.get<ApiClient[]>('/api/admin/api-clients');
  }

  createApiClient(name: string, description: string | null): Observable<ApiClientSecret> {
    return this.http.post<ApiClientSecret>('/api/admin/api-clients', { name, description });
  }

  updateApiClient(
    id: number,
    changes: { name?: string; description?: string; isEnabled?: boolean },
  ): Observable<ApiClient> {
    return this.http.patch<ApiClient>(`/api/admin/api-clients/${id}`, changes);
  }

  rotateApiClientSecret(id: number): Observable<ApiClientSecret> {
    return this.http.post<ApiClientSecret>(`/api/admin/api-clients/${id}/secret`, null);
  }

  deleteApiClient(id: number): Observable<unknown> {
    return this.http.delete(`/api/admin/api-clients/${id}`);
  }

  setCompanySync(companyId: string, syncEnabled: boolean): Observable<unknown> {
    return this.http.patch(`/api/admin/companies/${encodeURIComponent(companyId)}`, {
      syncEnabled,
    });
  }

  adminStatuses(): Observable<AppStatusUsage[]> {
    return this.http.get<AppStatusUsage[]>('/api/admin/statuses');
  }

  createStatus(status: AppStatusRequest): Observable<AppStatus> {
    return this.http.post<AppStatus>('/api/admin/statuses', status);
  }

  updateStatus(code: string, status: AppStatusRequest): Observable<AppStatus> {
    return this.http.put<AppStatus>(`/api/admin/statuses/${encodeURIComponent(code)}`, status);
  }

  deleteStatus(code: string): Observable<unknown> {
    return this.http.delete(`/api/admin/statuses/${encodeURIComponent(code)}`);
  }

  statusMappings(): Observable<StatusMapping[]> {
    return this.http.get<StatusMapping[]>('/api/admin/status-mappings');
  }

  unmappedStatuses(): Observable<{ rawStatus: string; documents: number }[]> {
    return this.http.get<{ rawStatus: string; documents: number }[]>(
      '/api/admin/status-mappings/unmapped',
    );
  }

  createStatusMapping(mapping: {
    rawStatus: string;
    normalized: NormalizedStatus;
    isFinal: boolean;
    isConfirmed: boolean;
    comment: string | null;
  }): Observable<StatusMapping> {
    return this.http.post<StatusMapping>('/api/admin/status-mappings', mapping);
  }

  updateStatusMapping(
    id: number,
    mapping: {
      normalized: NormalizedStatus;
      isFinal: boolean;
      isConfirmed: boolean;
      comment: string | null;
    },
  ): Observable<unknown> {
    return this.http.put(`/api/admin/status-mappings/${id}`, mapping);
  }

  private download(
    url: string,
    params: HttpParams,
    fallbackName: string,
  ): Observable<DownloadedFile> {
    return this.http.get(url, { params, responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>) => ({
        blob: response.body!,
        fileName: fileNameFrom(response.headers.get('Content-Disposition')) ?? fallbackName,
      })),
    );
  }
}

/** Nom de fichier d'un en-tête Content-Disposition (forme RFC 5987 prioritaire). */
export function fileNameFrom(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

/** Déclenche l'enregistrement d'un fichier dans le navigateur. */
export function saveFile(file: DownloadedFile): void {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
